import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    IconButton,
    useTheme,
    styled,
    Stack,
    Tooltip
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { alpha } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LoginIcon from '@mui/icons-material/Login';
import useAuth from '../hooks/useAuth';
import AddBalanceModal from '../pages/dashboard/operator/AddBalanceModal';
import AddManualTradeModal from '../pages/dashboard/operator/AddManualTradeModal';
import { PATH_DASHBOARD } from '../routes/paths';

const StyledCard = styled(Card)(({ theme, disabled }) => ({
    background: '#ffffff',
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: `0 2px 8px ${alpha(theme.palette.grey[500], 0.08)}`,
    opacity: disabled ? 0.6 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
    cursor: disabled ? 'not-allowed' : 'pointer',
    filter: disabled ? 'grayscale(40%)' : 'none',
    transition: 'all 0.2s ease-in-out',
    minWidth: '100%',
    width: '100%',
    overflow: 'visible', // Ensure content is not cut off
    '&:hover': {
        boxShadow: disabled ? `0 2px 8px ${alpha(theme.palette.grey[500], 0.08)}` : `0 4px 12px ${alpha(theme.palette.grey[500], 0.12)}`,
        transform: disabled ? 'none' : 'translateY(-1px)',
    },
}));

const CardHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    background: alpha(theme.palette.grey[50], 0.5),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    gap: theme.spacing(0.5),
    width: '100%',
    minWidth: '100%',
    overflow: 'visible',
    [theme.breakpoints.up('lg')]: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        padding: theme.spacing(0.75, 1.25),
    },
}));

const TopRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(0.5),
    minWidth: '100%', // Ensure full width usage
    [theme.breakpoints.up('lg')]: {
        justifyContent: 'flex-start',
        gap: theme.spacing(0.5),
    },
}));

const UserInfoSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start', // Changed to flex-start to accommodate vertical layout
    gap: theme.spacing(0.25),
    flex: '0 0 auto', // Don't grow or shrink
    minWidth: 'fit-content',
    maxWidth: '120px', // Reduced for mobile to accommodate metrics
    [theme.breakpoints.up('lg')]: {
        maxWidth: '150px',
        flex: '0 0 auto',
    },
}));

const MetricsSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.25),
    width: '100%',
    [theme.breakpoints.up('lg')]: {
        flex: 1,
        justifyContent: 'center',
        flexWrap: 'nowrap',
        gap: theme.spacing(0.5),
        width: 'auto',
    },
}));

const ActionsSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    flexShrink: 0,
    minWidth: 'fit-content', // Ensure buttons are always visible
    [theme.breakpoints.up('lg')]: {
        flex: '0 0 auto',
    },
}));

const UserNameChip = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: theme.spacing(0.4, 0.8), // Reduced padding for mobile
    borderRadius: theme.spacing(0.75),
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    minWidth: '80px', // Reduced for mobile
    maxWidth: '110px', // Reduced for mobile to fit with metrics
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        transform: 'translateY(-1px)',
        boxShadow: `0 3px 6px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
    [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(0.5, 1),
        minWidth: '90px',
        maxWidth: '130px',
    },
}));

const MetricCard = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    borderRadius: theme.spacing(0.75),
    padding: theme.spacing(0.4, 0.6),
    minWidth: '76px',
    backdropFilter: 'blur(4px)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 1)',
        transform: 'translateY(-1px)',
        boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
    [theme.breakpoints.up('lg')]: {
        minWidth: '90px',
        padding: theme.spacing(0.5, 0.9),
    },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    '& .MuiTableCell-root': {
        padding: '4px 8px',
        borderColor: theme.palette.divider,
        borderStyle: 'solid',
        borderWidth: '0 0 1px 1px',
        fontSize: '0.75rem',
        whiteSpace: 'nowrap',
        '&:last-child': {
            borderRight: `1px solid ${theme.palette.divider}`,
        }
    },
    '& .MuiTableRow-root:first-of-type .MuiTableCell-root': {
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiTableHead-root .MuiTableRow-root': {
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
        fontWeight: 600,
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
        color: theme.palette.text.secondary,
        fontSize: '0.7rem',
        letterSpacing: '0.5px',
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
        transition: 'background-color 0.2s ease-in-out',
    }
}));

const PLSummaryCard = ({
    username,
    userReferenceName,
    adminGrossPL,
    adminActivePL,
    adminBookedPL,
    adminBrokerage,
    sAdminGrossPL,
    sAdminActivePL,
    sAdminBookePL,
    sAdminBrokerage,
    brokerGrossPL,
    brokerActivePL,
    brokerBookedPL,
    brokerBrokerage,
    refundBrokerage,
    defaultExpanded = true,
    onExpandChange,
    onClick,
    disabled = false,
    isBrokerView = false,
    isAdminView = false,
    selectedBroker = null,
    selectedAdmin = null,
    onUserUpdate, // Add this prop for updating user data after actions
    userData = {}, // Add this prop to pass user data for modals
    showActionButtons = true, // Add control for showing/hiding action buttons
    isAdmin = false, // Add control for admin-specific actions
    onSettingsClick, // Add this prop for handling settings click
    onRedirectClick, // Add this prop for handling redirect click
    role = 'broker', // Add role prop to determine user type
    showDesktopMetrics = true // Add prop to control desktop metrics display
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const [openBalanceModal, setOpenBalanceModal] = useState(false);
    const [openTradeModal, setOpenTradeModal] = useState(false); const theme = useTheme();
    const { user } = useAuth(); // Get the authenticated user
    const navigate = useNavigate();
    const location = useLocation();// Settings click handler with navigation logic similar to UserConfigInfo
    const handleSettingsClick = (event) => {
        event.stopPropagation();

        if (onSettingsClick) {
            // If onSettingsClick prop is provided, use it
            onSettingsClick({ username, ...userData });
        } else {
            // Otherwise, use the navigation logic similar to UserConfigInfo
            let navigateToURL = '';
            if (user.role.rolename === 'admin') {
                navigateToURL = isBrokerView ? `/admin/client/edit/` : `/admin/broker/edit/`;
            } else if (user.role.rolename === 'superAdmin') {
                // SuperAdmin navigation based on current view state
                if (selectedBroker) {
                    // Viewing broker's clients - navigate to client edit
                    navigateToURL = `/superadmin/client/edit/`;
                } else if (selectedAdmin || isAdminView) {
                    // Viewing admin's brokers - navigate to broker edit
                    navigateToURL = `/superadmin/broker/edit/`;
                } else {
                    // Viewing admins - navigate to admin edit
                    navigateToURL = `/superadmin/admin/edit/`;
                }
            } else if (user.role.rolename === 'broker') {
                // For brokers, always navigate to client edit since they only see clients
                navigateToURL = `/broker/client/edit/`;
            } else {
                navigateToURL = `/broker/client/edit/`;
            }
            // Use trader._id if available, otherwise fall back to username
            const userId = userData?.trader?._id || userData?._id || username;
            navigate(`${navigateToURL}${userId}`);
        }
    };
    // console.log('PLSummaryCard rendered with userData:', isBrokerView);
    // Update local state when defaultExpanded changes
    useEffect(() => {
        setIsExpanded(defaultExpanded);
    }, [defaultExpanded]);

    const handleExpandClick = (event) => {
        // Prevent the click from propagating to the parent card
        event.stopPropagation();

        const newExpandedState = !isExpanded;
        setIsExpanded(newExpandedState);
        onExpandChange?.(newExpandedState);
    };


    // Modal handlers
    const handleOpenBalanceModal = () => {
        setOpenBalanceModal(true);
    };

    const handleCloseBalanceModal = () => {
        setOpenBalanceModal(false);
    };

    const handleOpenTradeModal = () => {
        setOpenTradeModal(true);
    };

    const handleCloseTradeModal = () => {
        setOpenTradeModal(false);
    };

    const formatValue = (value) => {
        return typeof value === 'string' ? value : value?.toLocaleString() || '-';
    };
    const getValueColor = (value) => {
        if (typeof value === 'string') {
            return value.includes('-') ? '#c62828' : '#1b5e20'; // Dark red and dark green
        }
        return value < 0 ? '#c62828' : '#1b5e20'; // Dark red and dark green
    };
    const rows = isBrokerView
        ? [
            { label: 'BROKER(%)', grossPL: adminGrossPL, activePL: adminActivePL, bookedPL: adminBookedPL, brokerage: adminBrokerage },
            { label: 'ADMIN(%)', grossPL: sAdminGrossPL, activePL: sAdminActivePL, bookedPL: sAdminBookePL, brokerage: sAdminBrokerage },
            { label: 'C. GROSS', grossPL: brokerGrossPL, activePL: brokerActivePL, bookedPL: brokerBookedPL, brokerage: brokerBrokerage }
        ]
        : [
            { label: 'ADMIN(%)', grossPL: adminGrossPL, activePL: adminActivePL, bookedPL: adminBookedPL, brokerage: adminBrokerage },
            { label: 'S-ADMIN(%)', grossPL: sAdminGrossPL, activePL: sAdminActivePL, bookedPL: sAdminBookePL, brokerage: sAdminBrokerage },
            { label: 'C. GROSS', grossPL: brokerGrossPL, activePL: brokerActivePL, bookedPL: brokerBookedPL, brokerage: brokerBrokerage }
        ];
    return (<StyledCard disabled={disabled}>

        <CardHeader>
            {/* Top Row: Username, Metrics (mobile), and Action Buttons */}
            <TopRow>
                {/* User Info Section */}
                <UserInfoSection>
                    <UserNameChip
                        onClick={(e) => {
                            if (disabled) return;
                            e.stopPropagation();

                            // Handle navigation based on role and view context - exactly matching BrokerDataTable patterns
                            // Handle navigation based on role and view context
                            if (isBrokerView && user.role.rolename === 'admin') {
                                // Admin is in broker view, clicking on client should navigate to broker/client/id
                                const clientId = userData?.trader?._id;
                                if (clientId) {
                                    navigate(PATH_DASHBOARD.insurerRole.broker.client.view(clientId));
                                }
                            } else if ((isBrokerView && user.role.rolename === 'broker') || user.role.rolename === 'broker') {
                                // Broker clicking on client should navigate to their client view
                                const clientId = userData?.trader?._id;
                                if (clientId) {
                                    navigate(PATH_DASHBOARD.operatorRole.clients.view(clientId));
                                }
                            }
                            else if (isBrokerView && user.role.rolename === 'superAdmin') {
                                // SuperAdmin is in broker view, clicking on client should navigate to client trades
                                const clientId = userData?.trader?._id;
                                if (clientId && selectedBroker && selectedAdmin) {
                                    navigate(`/superadmin/dashboard/${selectedAdmin}/broker/${selectedBroker}/client/${clientId}`);
                                }
                            }
                            else {
                                // Default behavior - call the onClick prop
                                onClick?.(`${username}`, userReferenceName);
                            }
                        }}
                    >
                        {/* Username - always on first line */}
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                lineHeight: 1.1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                width: '100%',
                            }}
                        >
                            <span style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1
                            }}>
                                {username}
                            </span>
                            {userData?.trader?.isDemoAccount === true && (
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-block',
                                        backgroundColor: '#f0f0f0',
                                        color: 'primary.light',
                                        padding: '2px 4px',
                                        borderRadius: '8px',
                                        fontSize: '0.55rem',
                                        fontWeight: 'bold',
                                        border: '1px solid #ccc',
                                        flexShrink: 0,
                                    }}
                                >
                                    DEMO
                                </Box>
                            )}
                        </Typography>
                        {/* Reference name - always on second line if present */}
                        {userReferenceName && userReferenceName !== '' && (
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.65rem',
                                    opacity: 0.85,
                                    fontWeight: 500,
                                    lineHeight: 1,
                                    marginTop: '2px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: '100%',
                                    maxWidth: { xs: '40px', lg: '70px' }, // Limit reference name length
                                }}
                            >
                                ({userReferenceName})
                            </Typography>
                        )}
                    </UserNameChip>
                </UserInfoSection>

                {/* Mobile Metrics Section - shown inline on mobile only */}
                <Box sx={{
                    display: { xs: 'flex', lg: 'none' },
                    flex: 1,
                    gap: theme.spacing(0.25),
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    {[
                        {
                            label: isBrokerView ? 'BROKER(%)' : 'ADMIN(%)',
                            value: adminGrossPL
                        },
                        {
                            label: isBrokerView ? 'ADMIN(%)' : 'S-ADMIN(%)',
                            value: sAdminGrossPL
                        },
                        {
                            label: 'CLIENT GROSS',
                            value: brokerGrossPL,
                            showRefund: refundBrokerage && parseFloat(String(refundBrokerage).replace(/[₹,]/g, '')) > 0
                        }
                    ].map(({ label, value, showRefund }, index) => (
                        <MetricCard key={label} sx={{ minWidth: '60px', padding: theme.spacing(0.3, 0.5) }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.55rem',
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px',
                                    mb: 0.1,
                                    textAlign: 'center'
                                }}
                            >
                                {label}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.65rem',
                                    color: formatValue(value) === '-0' ? '#1b5e20' : getValueColor(value),
                                    textAlign: 'center',
                                    lineHeight: 1.1
                                }}
                            >
                                {formatValue(value) === '-0' ? '0' : formatValue(value)}
                            </Typography>
                            {showRefund && label === 'CLIENT GROSS' && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.5rem',
                                        color: 'blue',
                                        fontWeight: 500,
                                        mt: 0.1,
                                        textAlign: 'center'
                                    }}
                                >
                                    RBrk: {formatValue(refundBrokerage)}
                                </Typography>
                            )}
                        </MetricCard>
                    ))}
                </Box>
                {/* Desktop Metrics Section - only shown on lg+ screens and when showDesktopMetrics is true */}
                {showDesktopMetrics && (
                    <Box sx={{
                        display: { xs: 'none', lg: 'flex' },
                        flex: 1,
                        justifyContent: 'center',
                        gap: 0.5
                    }}>
                        {[
                            {
                                label: isBrokerView ? 'BROKER(%)' : 'ADMIN(%)',
                                value: adminGrossPL
                            },
                            {
                                label: isBrokerView ? 'ADMIN(%)' : 'S-ADMIN(%)',
                                value: sAdminGrossPL
                            },
                            {
                                label: 'CLIENT GROSS',
                                value: brokerGrossPL,
                                showRefund: refundBrokerage && parseFloat(String(refundBrokerage).replace(/[₹,]/g, '')) > 0
                            }
                        ].map(({ label, value, showRefund }, index) => (
                            <MetricCard key={label}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.4px',
                                        mb: 0.1,
                                        textAlign: 'center'
                                    }}
                                >
                                    {label}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '0.7rem', lg: '0.72rem' },
                                        color: formatValue(value) === '-0' ? '#1b5e20' : getValueColor(value),
                                        textAlign: 'center',
                                        lineHeight: 1.1
                                    }}
                                >
                                    {formatValue(value) === '-0' ? '0' : formatValue(value)}
                                </Typography>
                                {showRefund && label === 'CLIENT GROSS' && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.55rem',
                                            color: 'blue',
                                            fontWeight: 500,
                                            mt: 0.1,
                                            textAlign: 'center'
                                        }}
                                    >
                                        RBrk: {formatValue(refundBrokerage)}
                                    </Typography>
                                )}
                            </MetricCard>
                        ))}
                    </Box>
                )}

                {/* Metric Values without labels - aligned with header when showDesktopMetrics is false */}
                {!showDesktopMetrics && (
                    <Box sx={{
                        display: { xs: 'none', lg: 'flex' },
                        flex: 1,
                        justifyContent: 'center',
                        gap: theme.spacing(2),
                        alignItems: 'center'
                    }}>
                        {[adminGrossPL, sAdminGrossPL, brokerGrossPL].map((value, index) => {
                            const isClientGross = index === 2;
                            const showRefund = isClientGross && refundBrokerage && parseFloat(String(refundBrokerage).replace(/[₹,]/g, '')) > 0;

                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        minWidth: '90px',
                                        textAlign: 'center',
                                        padding: theme.spacing(0.5),
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '0.72rem',
                                            color: formatValue(value) === '-0' ? '#1b5e20' : getValueColor(value),
                                            lineHeight: 1.1
                                        }}
                                    >
                                        {formatValue(value) === '-0' ? '0' : formatValue(value)}
                                    </Typography>
                                    {showRefund && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: '0.55rem',
                                                color: 'blue',
                                                fontWeight: 500,
                                                mt: 0.1,
                                                display: 'block'
                                            }}
                                        >
                                            RBrk: {formatValue(refundBrokerage)}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}                {/* Actions Section */}
                <ActionsSection sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    gap: { xs: 0.15, lg: 0.25 },
                    alignItems: 'center'
                }}>
                    {showActionButtons && (
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                            {(isBrokerView || user.role.rolename === 'broker') && (
                                <>
                                    <Tooltip title="Add Balance" placement="top">
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenBalanceModal();
                                            }}
                                            size="small"
                                            sx={{
                                                color: 'primary.main',
                                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                border: '1px solid',
                                                borderColor: 'primary.light',
                                                width: { xs: 22, lg: 24 },
                                                height: { xs: 22, lg: 24 },
                                                '&:hover': {
                                                    color: 'primary.dark',
                                                    backgroundColor: 'primary.lighter',
                                                    transform: 'scale(1.05)',
                                                },
                                                transition: 'all 0.2s ease-in-out',
                                            }}
                                        >
                                            <MonetizationOnIcon sx={{ fontSize: { xs: '0.7rem', lg: '0.8rem' } }} />
                                        </IconButton>
                                    </Tooltip>
                                    {user.role.rolename === "admin" && (
                                        <Tooltip title="Add Trade" placement="top">
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenTradeModal();
                                                }}
                                                size="small"
                                                sx={{
                                                    color: 'secondary.main',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                                    border: '1px solid',
                                                    borderColor: 'secondary.light',
                                                    width: { xs: 22, lg: 24 },
                                                    height: { xs: 22, lg: 24 },
                                                    '&:hover': {
                                                        color: 'secondary.dark',
                                                        backgroundColor: 'secondary.lighter',
                                                        transform: 'scale(1.05)',
                                                    },
                                                    transition: 'all 0.2s ease-in-out',
                                                }}
                                            >
                                                <AddIcon sx={{ fontSize: { xs: '0.7rem', lg: '0.8rem' } }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </>
                            )}
                            {/* {user.role.rolename !== 'superAdmin' && (<Tooltip title="Redirect Login" placement="top">
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onRedirectClick) {
                                            const traderId = userData?.trader?._id || userData?._id;
                                            onRedirectClick(traderId);
                                        }
                                    }}
                                    size="small"
                                    sx={{
                                        color: 'success.main',
                                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                        border: '1px solid',
                                        borderColor: 'success.light',
                                        width: { xs: 22, lg: 24 },
                                        height: { xs: 22, lg: 24 },
                                        '&:hover': {
                                            color: 'success.dark',
                                            backgroundColor: 'success.lighter',
                                            transform: 'scale(1.05)',
                                        },
                                        transition: 'all 0.2s ease-in-out',
                                    }}
                                >
                                    <LoginIcon sx={{ fontSize: { xs: '0.7rem', lg: '0.8rem' } }} />
                                </IconButton>
                            </Tooltip>)} */}
                            {/* Show Settings icon based on role: brokers only if canEditClient is true, others always */}
                            {user.role.rolename !== 'superAdmin' && (user.role.rolename === 'broker' ? user?.canEditClient : true) && (
                                <Tooltip title="Settings" placement="top">
                                    <IconButton
                                        size="small"
                                        onClick={handleSettingsClick}
                                        sx={{
                                            color: 'text.secondary',
                                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            width: { xs: 22, lg: 24 },
                                            height: { xs: 22, lg: 24 },
                                            '&:hover': {
                                                color: 'primary.main',
                                                backgroundColor: 'action.hover',
                                                transform: 'scale(1.05)',
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                        }}
                                    >
                                        <SettingsIcon sx={{ fontSize: { xs: '0.7rem', lg: '0.8rem' } }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    )}
                    <IconButton
                        onClick={handleExpandClick}
                        size="small"
                        sx={{
                            ml: { xs: 0, lg: 0.25 },
                            transition: 'transform 0.3s ease-in-out',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            border: '1px solid',
                            borderColor: 'divider',
                            width: { xs: 22, lg: 24 },
                            height: { xs: 22, lg: 24 },
                            '&:hover': {
                                backgroundColor: 'primary.lighter',
                                transform: isExpanded ? 'rotate(180deg) scale(1.1)' : 'rotate(0deg) scale(1.1)',
                            },
                        }}
                    >
                        <KeyboardArrowDownIcon sx={{ fontSize: { xs: '0.7rem', lg: '0.8rem' } }} />
                    </IconButton>
                </ActionsSection>

            </TopRow>        </CardHeader>

        {
            isExpanded && (
                <>
                    <StyledTableContainer>
                        <Table size="small" sx={{ tableLayout: 'fixed' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="20%">TYPE</TableCell>
                                    <TableCell align="right" width="20%">GROSS PL</TableCell>
                                    <TableCell align="right" width="20%">ACTIVE PL</TableCell>
                                    <TableCell align="right" width="20%">BOOKED PL</TableCell>
                                    <TableCell align="right" width="20%">BROKERAGE</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                                        <TableCell align="right" sx={{ color: formatValue(row.grossPL) === '-0' ? '#1b5e20' : getValueColor(row.grossPL), fontWeight: 1000 }}>
                                            {formatValue(row.grossPL) === '-0' ? '0' : formatValue(row.grossPL)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: formatValue(row.activePL) === '-0' ? '#1b5e20' : getValueColor(row.activePL), fontWeight: 1000 }}>
                                            {formatValue(row.activePL) === '-0' ? '0' : formatValue(row.activePL)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: getValueColor(row.bookedPL), fontWeight: 1000 }}>
                                            {formatValue(row.bookedPL)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: getValueColor(row.brokerage), fontWeight: 1000 }}>
                                            {formatValue(row.brokerage)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 2,
                            py: 0.5,
                            backgroundColor: alpha(theme.palette.primary.main, 0.02),
                            borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.text.secondary,
                                letterSpacing: '0.5px'
                            }}
                        >
                            REFUND BROKERAGE
                        </Typography>
                        <Box
                            sx={{
                                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                minWidth: '100px',
                                textAlign: 'right',
                                boxShadow: `inset 0 1px 2px ${alpha(theme.palette.common.black, 0.03)}`,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    color: 'blue',
                                }}
                            >
                                {formatValue(refundBrokerage)}
                            </Typography>
                        </Box>
                    </Box>
                </>)
        }
        {
            /* Only render balance modal in broker view */
            isBrokerView && openBalanceModal && (
                <AddBalanceModal
                    open={openBalanceModal}
                    balanceSummary={{
                        trader: {
                            username,
                            _id: userData?.trader?._id || userData?._id
                        },
                        // Use the actual balance fields from userData (the original API item)
                        balance: userData?.balance || 0,
                        activeProfitLoss: userData?.activeProfitLoss || 0,
                        totalProfit: userData?.totalProfit || 0,
                        totalBrokerage: userData?.totalBrokerage || 0,
                        blockedMargin: userData?.blockedMargin || 0,
                        grossPl: userData?.grossPl || 0,
                        yourGrossPl: userData?.yourGrossPl || 0,
                        superAdminGrossPl: userData?.superAdminGrossPl || 0,
                        yourActivePl: userData?.yourActivePl || 0,
                        yourProfitLoss: userData?.yourProfitLoss || 0,
                        yourBrokerage: userData?.yourBrokerage || 0,
                        superAdminActivePl: userData?.superAdminActivePl || 0,
                        superAdminProfit: userData?.superAdminProfit || 0,
                        superAdminBrokerage: userData?.superAdminBrokerage || 0,
                        refundBrokerage: userData?.refundBrokerage || 0,
                    }}
                    onClose={handleCloseBalanceModal}
                    onUserUpdate={onUserUpdate}
                />
            )
        }
        {
            /* Only render trade modal in broker view */
            isBrokerView && openTradeModal && (
                <AddManualTradeModal
                    open={openTradeModal}
                    client={{ username, ...userData?.trader }}
                    onClose={handleCloseTradeModal}
                    onUserUpdate={onUserUpdate}
                />
            )
        }
    </StyledCard >
    );
};

PLSummaryCard.propTypes = {
    username: PropTypes.string,
    userReferenceName: PropTypes.string,
    adminGrossPL: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    adminActivePL: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    adminBookedPL: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    adminBrokerage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sAdminGrossPL: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sAdminActivePL: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sAdminBookePL: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sAdminBrokerage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    brokerGrossPL: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    brokerActivePL: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    brokerBookedPL: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    brokerBrokerage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    refundBrokerage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    defaultExpanded: PropTypes.bool,
    onExpandChange: PropTypes.func,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    isBrokerView: PropTypes.bool,
    isAdminView: PropTypes.bool,
    selectedBroker: PropTypes.object,
    selectedAdmin: PropTypes.object,
    onUserUpdate: PropTypes.func,
    userData: PropTypes.object,
    showActionButtons: PropTypes.bool,
    isAdmin: PropTypes.bool,
    onSettingsClick: PropTypes.func,
    onRedirectClick: PropTypes.func,
    role: PropTypes.string
};

export default PLSummaryCard;
